import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { StoryId, getStoryDataForDay, StoryDayData, STORY_BOUNDARIES, getKeyDaysForStory } from './story-data';

@Injectable({
  providedIn: 'root'
})
export class StoryContextService {
  private currentStoryId$ = new BehaviorSubject<StoryId | null>(null);
  private currentDay$ = new BehaviorSubject<number>(1);

  constructor() {
    // Try to get story from URL or localStorage
    const savedStory = localStorage.getItem('demo-story-id');
    const savedDay = localStorage.getItem('demo-story-day');
    
    if (savedStory && (savedStory === 'jon' || savedStory === 'ben' || savedStory === 'jane' || savedStory === 'henry' || savedStory === 'sarah' || savedStory === 'marcus')) {
      this.currentStoryId$.next(savedStory as StoryId);
    }
    
    if (savedDay) {
      this.currentDay$.next(parseInt(savedDay, 10));
    }
  }

  setStory(storyId: StoryId | null, day: number = 1): void {
    this.currentStoryId$.next(storyId);
    this.currentDay$.next(day);
    
    if (storyId) {
      localStorage.setItem('demo-story-id', storyId);
      localStorage.setItem('demo-story-day', day.toString());
    } else {
      localStorage.removeItem('demo-story-id');
      localStorage.removeItem('demo-story-day');
    }
  }

  getCurrentStoryId(): Observable<StoryId | null> {
    return this.currentStoryId$.asObservable();
  }

  getCurrentDay(): Observable<number> {
    return this.currentDay$.asObservable();
  }

  getCurrentStoryIdValue(): StoryId | null {
    return this.currentStoryId$.value;
  }

  getCurrentDayValue(): number {
    return this.currentDay$.value;
  }

  getCurrentStoryData(): StoryDayData | null {
    const storyId = this.currentStoryId$.value;
    const day = this.currentDay$.value;
    
    if (!storyId) return null;
    
    return getStoryDataForDay(storyId, day);
  }

  incrementDay(): void {
    const currentDay = this.currentDay$.value;
    const storyId = this.currentStoryId$.value;
    const nextKeyDay = this.getNextKeyDay(storyId, currentDay);
    
    if (nextKeyDay !== null) {
      this.setStory(storyId, nextKeyDay);
    } else {
      // If no next key day, just increment by 1 (fallback)
      this.setStory(storyId, currentDay + 1);
    }
  }

  decrementDay(): void {
    const currentDay = this.currentDay$.value;
    const storyId = this.currentStoryId$.value;
    const previousKeyDay = this.getPreviousKeyDay(storyId, currentDay);
    
    if (previousKeyDay !== null) {
      this.setStory(storyId, previousKeyDay);
    } else {
      // If no previous key day, just decrement by 1 (fallback)
      const minDay = this.getMinDay(storyId);
      if (currentDay > minDay) {
        this.setStory(storyId, currentDay - 1);
      }
    }
  }

  /**
   * Get the next key day (day with events) after the current day
   */
  getNextKeyDay(storyId: StoryId | null, currentDay: number): number | null {
    if (!storyId) return null;
    
    const keyDays = getKeyDaysForStory(storyId);
    const maxDay = this.getMaxDay(storyId);
    
    // Find the first key day that is greater than currentDay
    const nextKeyDay = keyDays.find(day => day > currentDay && day <= maxDay);
    
    return nextKeyDay !== undefined ? nextKeyDay : null;
  }

  /**
   * Get the previous key day (day with events) before the current day
   */
  getPreviousKeyDay(storyId: StoryId | null, currentDay: number): number | null {
    if (!storyId) return null;
    
    const keyDays = getKeyDaysForStory(storyId);
    const minDay = this.getMinDay(storyId);
    
    // Find the last key day that is less than currentDay
    const previousKeyDays = keyDays.filter(day => day < currentDay && day >= minDay);
    
    return previousKeyDays.length > 0 ? previousKeyDays[previousKeyDays.length - 1] : null;
  }

  getMinDay(storyId: StoryId | null): number {
    if (!storyId) return 1;
    return STORY_BOUNDARIES[storyId]?.minDay || 1;
  }

  getMaxDay(storyId: StoryId | null): number {
    if (!storyId) return 270;
    return STORY_BOUNDARIES[storyId]?.maxDay || 270;
  }

  canGoPrevious(storyId: StoryId | null, day: number): boolean {
    if (!storyId) return false;
    return day > this.getMinDay(storyId);
  }

  canGoNext(storyId: StoryId | null, day: number): boolean {
    if (!storyId) return false;
    return day < this.getMaxDay(storyId);
  }
}

